// =============================
// Imports
// =============================

// External dependencies
import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { NextSeo } from 'next-seo';

// Config
import { i18n, withTranslation } from '../../config/i18n';

// Actions
import storeWrapper from '../../store';
import { getSuggestedPlaylists } from '../../store/actions/SuggestedActions';

// Components
import PrivateRoute from '../../components/containers/privateRoute';
import SuggestedPlaylists from '../../components/layouts/suggested/playlists';

// Helpers
import { MewoError } from '../../helpers/errors';
import { getPlaylistCoverUrl } from '../../helpers/entity/playlist';
import { getWebsiteTitle } from '../../helpers/misc';
import * as pth from '../../helpers/proptypes';

// =============================
// Page
// =============================

class SuggestedPlaylistsPage extends PureComponent {
  static propTypes = {
    config: pth.config.isRequired,
    i18n: PropTypes.shape({
      language: PropTypes.string,
    }).isRequired,
    suggestedPlaylists: PropTypes.arrayOf(pth.suggestedplaylist).isRequired,
    t: PropTypes.func.isRequired,
    xHost: PropTypes.string.isRequired,
  };

  static getInitialProps = storeWrapper.getInitialPageProps(store => async () => {
    if (!store.getState().suggested.playlists.isFetchedOnce) {
      await store.dispatch(getSuggestedPlaylists());
    }

    if (!store.getState().suggested.playlists.data.length) {
      throw new MewoError({
        message: i18n.t('errors:suggestions.no_playlists'),
        statusCode: 404,
        type: 'NO_PLAYLISTS_SUGGESTIONS',
      });
    }

    return {
      namespacesRequired: ['common', 'pages', 'components', 'errors'],
    };
  });

  render() {
    const {
      config,
      i18n: { language },
      suggestedPlaylists,
      t,
      xHost,
    } = this.props;

    const pageUrl = `https://${xHost}/suggested/playlists`;

    const pageTitle = getWebsiteTitle(config, t('pages:suggested_playlists.meta_title'));
    const pageDescription = t('pages:suggested_playlists.meta_desc');
    const pageImages = [];

    for (let i = 0, len = suggestedPlaylists.length; i < len && !pageImages.length; i += 1) {
      if (getPlaylistCoverUrl(suggestedPlaylists[i], 'large')) {
        pageImages.push({
          url: getPlaylistCoverUrl(suggestedPlaylists[i], 'large'),
          width: 500,
          height: 500,
        });

        break;
      }
    }

    return (
      <PrivateRoute>
        <NextSeo
          title={pageTitle}
          description={pageDescription}
          canonical={pageUrl}
          openGraph={{
            type: 'website',
            url: pageUrl,
            title: pageTitle,
            description: pageDescription,
            images: pageImages,
          }}
        />
        <SuggestedPlaylists suggestedPlaylists={suggestedPlaylists} locale={language} t={t} />
      </PrivateRoute>
    );
  }
}

function mapStateToProps({ core, suggested }) {
  return {
    suggestedPlaylists: suggested.playlists.data,
    xHost: core.serverContext.xHost,
  };
}

export default compose(
  withTranslation(['pages', 'errors']),
  connect(mapStateToProps),
)(SuggestedPlaylistsPage);
